import React, { useState } from "react"
import { manageCatvCreate } from "../../../../api/manage_catv_create"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"
import { Checkbox, FormControlLabel } from "@mui/material"
import { SENSITIVITY_OPTIONS } from "../../../../constants/sensitivity"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

interface ManageCatvCreateProps {
  onClose: (num: number) => void // モーダルを閉じるためのコールバック
}

const ManageCatvCreate: React.FC<ManageCatvCreateProps> = ({ onClose }) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    sensitivityEnabled: false,
    sensitivity: "96",
    detectionEnabled: false,
    detectionPeriodStart: "00:00", // 不在検知時間帯のデフォルト値
    detectionPeriodEnd: "00:00", // 不在検知時間帯のデフォルト値
    optionEnabled: false,
    optionPeriodStart: "00:00", // 不在検知時間帯（オプション）のデフォルト値
    optionPeriodEnd: "00:00", // 不在検知時間帯（オプション）のデフォルト値
    criticalCountEnabled: false,
    criticalCount: "0" // 不在検知回数のデフォルト値
  })

  const [errors, setErrors] = useState({
    name: "",
    address: "",
    phone: "",
    sensitivity: "",
    detectionPeriodStart: "",
    detectionPeriodEnd: "",
    optionPeriodStart: "",
    optionPeriodEnd: "",
    criticalCount: "",
    server: ""
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement // 明示的に型キャスト
    const { name, value, type, checked } = target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const validate = () => {
    const validationErrors = { ...errors }
    let hasError = false

    // 絵文字を除外するための正規表現
    const emojiRegex = /(?:\p{Extended_Pictographic}(?![\u3190-\u319F\u3300-\u33FF]))+/gu

    // 必須項目のバリデーション
    if (!formData.name) {
      validationErrors.name = "必須項目が入力されていません"
      hasError = true
    } else if (formData.name.length > 64) {
      validationErrors.name = "入力文字数は64文字以内です"
      hasError = true
    } else if (emojiRegex.test(formData.name)) {
      validationErrors.name = "絵文字は使用できません"
      hasError = true
    } else {
      validationErrors.name = ""
    }

    if (!formData.address) {
      validationErrors.address = "必須項目が入力されていません"
      hasError = true
    } else if (formData.address.length > 256) {
      validationErrors.address = "入力文字数は256文字以内です"
      hasError = true
    } else if (emojiRegex.test(formData.address)) {
      validationErrors.address = "絵文字は使用できません"
      hasError = true
    } else {
      validationErrors.address = ""
    }

    if (!formData.phone) {
      validationErrors.phone = "必須項目が入力されていません"
      hasError = true
    } else if (formData.phone.length > 14) {
      validationErrors.phone = "入力文字数は14文字以内です"
      hasError = true
    } else if (emojiRegex.test(formData.phone)) {
      validationErrors.phone = "絵文字は使用できません"
      hasError = true
    } else {
      validationErrors.phone = ""
    }

    // チェックボックスがオンの場合のバリデーション
    if (formData.sensitivityEnabled && formData.sensitivity === "") {
      validationErrors.sensitivity = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.sensitivity = ""
    }

    if (formData.detectionEnabled && (formData.detectionPeriodStart === "" || formData.detectionPeriodEnd === "")) {
      validationErrors.detectionPeriodStart = "必須項目が入力されていません"
      validationErrors.detectionPeriodEnd = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.detectionPeriodStart = ""
      validationErrors.detectionPeriodEnd = ""
    }

    if (formData.optionEnabled && (formData.optionPeriodStart === "" || formData.optionPeriodEnd === "")) {
      validationErrors.optionPeriodStart = "必須項目が入力されていません"
      validationErrors.optionPeriodEnd = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.optionPeriodStart = ""
      validationErrors.optionPeriodEnd = ""
    }

    if (formData.criticalCountEnabled && formData.criticalCount === "") {
      validationErrors.criticalCount = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.criticalCount = ""
    }

    validationErrors.server = ""

    setErrors(validationErrors)
    return !hasError
  }

  const convertEnabledValues = (data: any) => {
    return {
      ...data,
      sensitivityEnabled: data.sensitivityEnabled ? 1 : 0,
      detectionEnabled: data.detectionEnabled ? 1 : 0,
      optionEnabled: data.optionEnabled ? 1 : 0,
      criticalCountEnabled: data.criticalCountEnabled ? 1 : 0
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      // バリデーション成功時の処理
      try {
        startLoading()
        const convertedData = convertEnabledValues(formData)
        await manageCatvCreate(
          convertedData.name,
          convertedData.address,
          convertedData.phone,
          convertedData.sensitivityEnabled,
          convertedData.sensitivity,
          convertedData.detectionEnabled,
          convertedData.detectionPeriodStart,
          convertedData.detectionPeriodEnd,
          convertedData.optionEnabled,
          convertedData.optionPeriodStart,
          convertedData.optionPeriodEnd,
          convertedData.criticalCountEnabled,
          convertedData.criticalCount
        )
        onClose(1)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ時にトップページへリダイレクト
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = {
            name: "",
            address: "",
            phone: "",
            sensitivity: "",
            detectionPeriodStart: "",
            detectionPeriodEnd: "",
            optionPeriodStart: "",
            optionPeriodEnd: "",
            criticalCount: "",
            server: ""
          }
          validationErrors.server = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = {
            name: "",
            address: "",
            phone: "",
            sensitivity: "",
            detectionPeriodStart: "",
            detectionPeriodEnd: "",
            optionPeriodStart: "",
            optionPeriodEnd: "",
            criticalCount: "",
            server: ""
          }
          validationErrors.server = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "8px 16px",
            boxSizing: "border-box"
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" style={{ flex: 1, textAlign: "left" }}>
            サービス提供元を追加
          </MDTypography>
          <button
            className="close-button"
            onClick={() => onClose(0)}
            style={{
              position: "absolute",
              right: 16,
              top: 8,
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </div>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="サービス提供元名"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.name && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.name}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="住所"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.address && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.address}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="電話番号"
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.phone && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.phone}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Checkbox name="sensitivityEnabled" checked={formData.sensitivityEnabled} onChange={handleChange} />
              }
              label="感度設定を有効化"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDInput
              select
              SelectProps={{ native: true }}
              label="感度"
              name="sensitivity"
              value={formData.sensitivity}
              onChange={handleChange}
              disabled={!formData.sensitivityEnabled}
              sx={{ width: "100%" }}
            >
              {SENSITIVITY_OPTIONS.map((option) => (
                <option key={option.value} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
            </MDInput>
          </MDBox>
          {errors.sensitivity && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.sensitivity}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={<Checkbox name="detectionEnabled" checked={formData.detectionEnabled} onChange={handleChange} />}
              label="不在検知時間帯を有効化"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDInput
              type="time"
              name="detectionPeriodStart"
              value={formData.detectionPeriodStart}
              onChange={handleChange}
              sx={{ width: "160px" }}
              disabled={!formData.detectionEnabled}
            />
            〜
            <MDInput
              type="time"
              name="detectionPeriodEnd"
              value={formData.detectionPeriodEnd}
              onChange={handleChange}
              sx={{ width: "160px" }}
              disabled={!formData.detectionEnabled}
            />
          </MDBox>
          {errors.detectionPeriodStart && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.detectionPeriodStart}
            </MDBox>
          )}
          {errors.detectionPeriodEnd && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.detectionPeriodEnd}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={<Checkbox name="optionEnabled" checked={formData.optionEnabled} onChange={handleChange} />}
              label="不在検知時間帯(オプション)を有効化"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDInput
              type="time"
              name="optionPeriodStart"
              value={formData.optionPeriodStart}
              onChange={handleChange}
              sx={{ width: "160px" }}
              disabled={!formData.optionEnabled}
            />
            〜
            <MDInput
              type="time"
              name="optionPeriodEnd"
              value={formData.optionPeriodEnd}
              onChange={handleChange}
              sx={{ width: "160px" }}
              disabled={!formData.optionEnabled}
            />
          </MDBox>
          {errors.optionPeriodStart && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.optionPeriodStart}
            </MDBox>
          )}
          {errors.optionPeriodEnd && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.optionPeriodEnd}
            </MDBox>
          )}
          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              登録
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageCatvCreate
